import type { MutationDef, MutationId, PickedMutation, Rarity } from './types';
import {
  ALL_MUTATIONS,
  commonMutations,
  rareMutations,
  epicMutations,
} from './catalog';

export const RARITY_WEIGHTS: Record<Rarity, number> = {
  common: 0.6,
  rare: 0.3,
  epic: 0.1,
};

export const MAX_DUPLICATES = 2;

type Rng = () => number;

function rollRarity(rng: Rng): Rarity {
  const r = rng();
  if (r < RARITY_WEIGHTS.common) return 'common';
  if (r < RARITY_WEIGHTS.common + RARITY_WEIGHTS.rare) return 'rare';
  return 'epic';
}

/** Conta quantas vezes cada mutation já foi pegada na run. */
function countMap(picks: ReadonlyArray<PickedMutation>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of picks) counts[p.id] = (counts[p.id] ?? 0) + 1;
  return counts;
}

/**
 * Gera 3 mutations únicas dentre as elegíveis, respeitando MAX_DUPLICATES.
 * Se não há mutações suficientes da raridade rolada, faz fallback para outra raridade.
 */
export function rollPicks(rng: Rng, currentPicks: ReadonlyArray<PickedMutation>): MutationDef[] {
  const counts = countMap(currentPicks);
  const eligible = (m: MutationDef): boolean => (counts[m.id] ?? 0) < MAX_DUPLICATES;

  const result: MutationDef[] = [];
  const tried: Set<MutationId> = new Set();
  let safety = 0;
  while (result.length < 3 && safety++ < 20) {
    const rarity = rollRarity(rng);
    const pool = poolFor(rarity).filter(eligible).filter((m) => !tried.has(m.id));
    let chosen: MutationDef | null = null;
    if (pool.length > 0) {
      chosen = pool[Math.floor(rng() * pool.length)]!;
    } else {
      // Fallback: pick anything eligible
      const all = ALL_MUTATIONS.filter(eligible).filter((m) => !tried.has(m.id));
      if (all.length === 0) break;
      chosen = all[Math.floor(rng() * all.length)]!;
    }
    tried.add(chosen.id);
    if (!result.includes(chosen)) result.push(chosen);
  }
  return result;
}

function poolFor(rarity: Rarity): MutationDef[] {
  switch (rarity) {
    case 'common': return commonMutations();
    case 'rare': return rareMutations();
    case 'epic': return epicMutations();
  }
}
